import React, { useState, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Skeleton,
  InputAdornment,
  Tooltip,
  Grid
} from '@mui/material';
import { 
  Person as PersonIcon, 
  Email as EmailIcon, 
  Work as WorkIcon, 
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Upload as UploadIcon,
  Settings as SettingsIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { getUserSettings, updateUserSettings, uploadResume } from '../services/api';
import { getCurrentUser } from '../services/auth';
import * as pdfjsLib from 'pdfjs-dist';

// Initialize PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

interface UserSettings {
  name: string;
  email: string | null;
  careerStage: 'new_grad' | 'junior' | 'senior' | 'staff';
  interestedRoles: string[];
  resumeText: string;
  resumeUrl?: string;
  resumeFilename?: string;
}

const SettingsSkeleton: React.FC = () => (
  <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: 'background.default' }}>
    <Card elevation={0} sx={{ 
      width: '100%',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 0,
      overflow: 'hidden'
    }}>
      <CardContent sx={{ width: '100%', maxWidth: 1200, mx: 'auto', p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
          <SettingsIcon color="primary" />
          <Typography variant="h5" fontWeight="600">
            Settings
          </Typography>
        </Stack>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              <Stack spacing={2}>
                <Skeleton variant="text" width="30%" height={24} />
                <Skeleton variant="rectangular" height={56} />
              </Stack>
              <Stack spacing={2}>
                <Skeleton variant="text" width="30%" height={24} />
                <Skeleton variant="rectangular" height={56} />
              </Stack>
              <Stack spacing={2}>
                <Skeleton variant="text" width="30%" height={24} />
                <Skeleton variant="rectangular" height={56} />
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              <Stack spacing={2}>
                <Skeleton variant="text" width="40%" height={24} />
                <Skeleton variant="rectangular" height={56} />
              </Stack>
              <Stack spacing={2}>
                <Skeleton variant="text" width="40%" height={24} />
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {[1, 2, 3].map((index) => (
                    <Skeleton key={index} variant="rectangular" width={100} height={32} />
                  ))}
                </Box>
              </Stack>
              <Stack spacing={2}>
                <Skeleton variant="text" width="40%" height={24} />
                <Skeleton variant="rectangular" height={200} />
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  </Box>
);

const Settings: React.FC = () => {
  const [settings, setSettings] = useState<UserSettings>({
    name: '',
    email: null,
    careerStage: 'new_grad',
    interestedRoles: [],
    resumeText: '',
    resumeUrl: undefined,
    resumeFilename: undefined
  });
  const [newRole, setNewRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [parsingFile, setParsingFile] = useState(false);

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

  useEffect(() => {
    const initializeSettings = async () => {
      try {
        // First get the user email
        const user = await getCurrentUser();
        if (user?.email) {
          setSettings(prev => ({
            ...prev,
            email: user.email || null
          }));
        }

        // Then load settings from backend
        await loadSettings();
      } catch (err) {
        console.error('Error initializing settings:', err);
        setError('Failed to load settings');
      }
    };

    initializeSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await getUserSettings();
      setSettings(prev => ({
        ...data,
        email: prev.email, // Keep the email from Firebase auth
        interestedRoles: data.interestedRoles || []
      }));
      
      if (data.resumeUrl) {
        try {
          const user = await getCurrentUser();
          if (!user) {
            throw new Error('User not authenticated');
          }
          
          const token = await user.getIdToken(true);
          
          const response = await fetch(data.resumeUrl, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (!response.ok) {
            if (response.status === 401) {
              const newToken = await user.getIdToken(true);
              const retryResponse = await fetch(data.resumeUrl, {
                headers: {
                  'Authorization': `Bearer ${newToken}`
                }
              });
              
              if (!retryResponse.ok) {
                throw new Error(`Failed to fetch resume: ${retryResponse.statusText}`);
              }
              
              const blob = await retryResponse.blob();
              setResumeFile(new File([blob], data.resumeFilename || 'resume.pdf', { type: blob.type }));
            } else {
              throw new Error(`Failed to fetch resume: ${response.statusText}`);
            }
          } else {
            const blob = await response.blob();
            setResumeFile(new File([blob], data.resumeFilename || 'resume.pdf', { type: blob.type }));
          }
        } catch (fileError) {
          console.error('Error fetching resume file:', fileError);
          setError('Unable to load your resume file. You can upload a new one.');
        }
      }
      setError(null);
    } catch (err) {
      setError('Failed to load settings');
      console.error('Error loading settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      if (resumeFile) {
        const { url } = await uploadResume(resumeFile);
        setSettings(prev => ({
          ...prev,
          resumeUrl: url
        }));
      }

      // Create a copy of settings with non-null email for API
      const settingsForApi = {
        ...settings,
        email: settings.email || '' // Ensure email is never null when sending to API
      };

      await updateUserSettings(settingsForApi);
      setSuccess('Settings saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to save settings');
      console.error('Error saving settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRole = () => {
    if (newRole && !(settings.interestedRoles || []).includes(newRole)) {
      setSettings(prev => ({
        ...prev,
        interestedRoles: [...(prev.interestedRoles || []), newRole]
      }));
      setNewRole('');
    }
  };

  const handleRemoveRole = (role: string) => {
    setSettings(prev => ({
      ...prev,
      interestedRoles: prev.interestedRoles.filter(r => r !== role)
    }));
  };

  const extractTextFromPDF = async (file: File): Promise<string> => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let fullText = '';
      
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const items = textContent.items as any[];
        
        // Sort items by vertical position (top to bottom)
        items.sort((a, b) => {
          const yDiff = Math.abs(a.transform[5] - b.transform[5]);
          if (yDiff < 5) { // Items on the same line
            return a.transform[4] - b.transform[4]; // Sort by x position
          }
          return b.transform[5] - a.transform[5]; // Sort by y position
        });

        let currentY = null;
        let lineText = '';
        
        for (const item of items) {
          const y = Math.round(item.transform[5]);
          
          // If we're on a new line
          if (currentY === null || Math.abs(y - currentY) > 5) {
            if (lineText) {
              fullText += lineText.trim() + '\n';
            }
            lineText = item.str;
            currentY = y;
          } else {
            // Add space between words on the same line
            const xDiff = item.transform[4] - (items[items.indexOf(item) - 1]?.transform[4] || 0);
            if (xDiff > 10) { // If there's significant horizontal space
              lineText += ' ' + item.str;
            } else {
              lineText += item.str;
            }
          }
        }
        
        // Add the last line
        if (lineText) {
          fullText += lineText.trim() + '\n';
        }
        
        // Add extra spacing between pages
        fullText += '\n\n';
      }
      
      return fullText.trim();
    } catch (error) {
      console.error('Error extracting text from PDF:', error);
      throw new Error('Failed to extract text from PDF');
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      setError('File size exceeds 10MB limit. Please choose a smaller file.');
      event.target.value = ''; // Clear the file input
      return;
    }

    if (file.type !== 'application/pdf') {
      setError('Please upload a PDF file only.');
      event.target.value = ''; // Clear the file input
      return;
    }

    setResumeFile(file);
    setParsingFile(true);
    setError(null);

    try {
      // Extract text from the PDF file
      const text = await extractTextFromPDF(file);

      // Update settings with the extracted text
      setSettings(prev => ({
        ...prev,
        resumeText: text
      }));

      setSuccess('PDF processed successfully. Click "Save Changes" to update your settings.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error handling file:', err);
      setError(err instanceof Error ? err.message : 'Failed to process PDF file. Please try again.');
    } finally {
      setParsingFile(false);
    }
  };

  const handlePreview = () => {
    setPreviewOpen(true);
  };

  if (loading) {
    return <SettingsSkeleton />;
  }

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Card elevation={0} sx={{ 
        width: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 0,
        overflow: 'hidden'
      }}>
        <CardContent sx={{ width: '100%', maxWidth: 1200, mx: 'auto', p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
            <SettingsIcon color="primary" />
            <Typography variant="h5" fontWeight="600">
              Settings
            </Typography>
          </Stack>

          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 3 }}
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => setError(null)}
                >
                  <DeleteIcon fontSize="inherit" />
                </IconButton>
              }
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert 
              severity="success" 
              sx={{ mb: 3 }}
              icon={<CheckCircleIcon />}
            >
              {success}
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Name"
                  value={settings.name}
                  onChange={(e) => setSettings(prev => ({ ...prev, name: e.target.value }))}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Email"
                  value={settings.email || ''}
                  disabled
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  helperText="Email cannot be changed"
                />

                <FormControl fullWidth>
                  <InputLabel>Career Stage</InputLabel>
                  <Select
                    value={settings.careerStage}
                    onChange={(e) => setSettings(prev => ({ ...prev, careerStage: e.target.value as UserSettings['careerStage'] }))}
                    label="Career Stage"
                    startAdornment={
                      <InputAdornment position="start">
                        <WorkIcon color="action" />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="new_grad">New Graduate</MenuItem>
                    <MenuItem value="junior">Junior</MenuItem>
                    <MenuItem value="senior">Senior</MenuItem>
                    <MenuItem value="staff">Staff</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Interested Roles"
                  placeholder="Add a role..."
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddRole()}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          variant="contained"
                          onClick={handleAddRole}
                          startIcon={<AddIcon />}
                          size="small"
                        >
                          Add
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {(settings.interestedRoles || []).map((role) => (
                    <Chip
                      key={role}
                      label={role}
                      onDelete={() => handleRemoveRole(role)}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>

                <Stack spacing={2}>
                  <Typography variant="subtitle1" fontWeight="500">
                    Resume
                  </Typography>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: '1px dashed',
                      borderColor: 'divider',
                      borderRadius: 1,
                      textAlign: 'center'
                    }}
                  >
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                      id="resume-upload"
                    />
                    <label htmlFor="resume-upload">
                      <Button
                        component="span"
                        variant="outlined"
                        startIcon={<UploadIcon />}
                        disabled={parsingFile}
                      >
                        {parsingFile ? 'Processing...' : 'Upload PDF Resume'}
                      </Button>
                    </label>
                    {resumeFile && (
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          {resumeFile.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          (PDF only, max 10MB)
                        </Typography>
                        <Button
                          variant="text"
                          color="primary"
                          onClick={handlePreview}
                          size="small"
                          startIcon={<VisibilityIcon />}
                          sx={{ ml: 1 }}
                        >
                          View
                        </Button>
                      </Stack>
                    )}
                  </Paper>
                </Stack>
              </Stack>
            </Grid>
          </Grid>

          <Box sx={{ 
            mt: 4, 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'center', 
            gap: 2 
          }}>
            <Button
              variant="outlined"
              onClick={handlePreview}
              startIcon={<VisibilityIcon />}
              size="large"
              fullWidth
            >
              View Current Resume
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              size="large"
              fullWidth
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Resume Preview</DialogTitle>
        <DialogContent>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              bgcolor: 'background.default',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              whiteSpace: 'pre-wrap'
            }}
          >
            {settings.resumeText}
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings;